import { Box } from '@material-ui/core';
import { PaymentMethodType } from '@nemuru-eng/js-api-client';

import { PaymentMethodSummaryFactory } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/ApplicationSummaryPaymentMethods/components/PaymentMethodSummaryFactory';
import { CustomCartAlert } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/CartAlerts/components/CustomCartAlert';
import { SummaryTitle } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/SummaryTitle';
import { Section } from '@/components/modules/Application/components/_shared/components/Section/Section';
import {
  DialogStepContent,
  DialogStepProps,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/components/DialogStepContent';
import { PaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { AbstractLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/entities/PaymentMethodDetail/AbstractLoanPaymentMethodDetailEntity';
import { SinglePaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/SinglePayment/_shared/entities/PaymentMethodDetail/SinglePaymentMethodDetailEntity';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useScreen } from '@/hooks/useScreen';

interface ConfirmNewAvailablePaymentMethodsProps extends DialogStepProps {
  paymentMethodDetailEntities: PaymentMethodDetailEntity[];
  newAvailablePaymentMethods: PaymentMethodType[];
}

export const ConfirmNewAvailablePaymentMethods = ({
  onNext,
  onPrev,
  paymentMethodDetailEntities,
  newAvailablePaymentMethods,
}: ConfirmNewAvailablePaymentMethodsProps) => {
  const { getTextIdTranslation } = useTranslation();
  const { sm } = useScreen();

  const hasAvailablePaymentMethods = !!(newAvailablePaymentMethods?.length && newAvailablePaymentMethods?.length > 0);

  return (
    <DialogStepContent
      titleTextId={'NEW_PAYMENT_METHODS_CONFIRMATION'}
      nextButtonTextId={'CONFIRM'}
      onNext={onNext}
      onPrev={onPrev}
      disabled={!hasAvailablePaymentMethods}
    >
      <Section>
        <FlexColumn gridGap={30}>
          <Font variant={'body2'} color={'textPrimary'}>
            <Text tId={'NEW_PAYMENT_METHODS_CONFIRMATION_NOTE'} />
          </Font>
          <Box display={'grid'} gridTemplateColumns={sm ? '7fr 5fr' : '1fr'} gridGap={24} alignItems={'start'}>
            <CustomPaper fill="paper">
              <SummaryTitle title={'PAYMENT_METHODS'} />
              {paymentMethodDetailEntities.map((paymentMethodDetailEntity) => {
                const paymentMethodEntity = (
                  paymentMethodDetailEntity as AbstractLoanPaymentMethodDetailEntity | SinglePaymentMethodDetailEntity
                ).paymentMethod;

                if (!paymentMethodEntity) {
                  return null;
                }

                return (
                  <PaymentMethodSummaryFactory
                    key={paymentMethodDetailEntity.type}
                    paymentMethodEntity={paymentMethodEntity}
                    available={newAvailablePaymentMethods.includes(paymentMethodDetailEntity.type)}
                  />
                );
              })}
            </CustomPaper>
            <FlexColumn gridGap={'16px'}>
              {hasAvailablePaymentMethods ? (
                <CustomCartAlert severity="error" icon="error">
                  {getTextIdTranslation('NEW_PAYMENT_METHODS_CONFIRMATION_CHANGE_ALERT')}
                </CustomCartAlert>
              ) : (
                <CustomCartAlert severity="error" icon="error">
                  {getTextIdTranslation('NEW_PAYMENT_METHODS_CONFIRMATION_NOT_COMPATIBLE_ALERT')}
                </CustomCartAlert>
              )}
            </FlexColumn>
          </Box>
          {hasAvailablePaymentMethods ? (
            <Font variant={'body2'} color={'textPrimary'}>
              <p>
                <b>{getTextIdTranslation('NEW_PAYMENT_METHODS_CONFIRMATION_CHANGE_DESCRIPTION')}</b>
              </p>
            </Font>
          ) : (
            <Font variant={'body2'} color={'textPrimary'}>
              <p>
                <b>{getTextIdTranslation('NEW_PAYMENT_METHODS_CONFIRMATION_NOT_COMPATIBLE_DESCRIPTION')}</b>
              </p>
            </Font>
          )}
        </FlexColumn>
      </Section>
    </DialogStepContent>
  );
};
